import React, { useState, useEffect } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useHazardAlerts } from '../../hooks/useHazardAlerts';

import hazardBlockImg from '../../assets/icons/hazard_block.png';
import hazardOpenImg from '../../assets/icons/hazard_open.png';

// Custom hazard icons
const iconConfig = {
  iconSize: [45, 36],
  iconAnchor: [22.5, 18],
  popupAnchor: [0, -20],
};

const hazardBlockIcon = L.icon({
  ...iconConfig,
  iconUrl: hazardBlockImg,
});

const hazardOpenIcon = L.icon({
  ...iconConfig,
  iconUrl: hazardOpenImg,
});

const HazardMarkers = ({ devices = [] }) => {
  const { alerts, connected, loading, deactivateAlert } = useHazardAlerts();
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Refresh "ago" time every 10 seconds
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  if (loading && alerts.length === 0) return null;

  const handleDeactivate = async (id) => {
    setDeactivatingId(id);
    const success = await deactivateAlert(id);
    if (!success) {
      alert('Failed to deactivate alert. Please try again.');
      setDeactivatingId(null);
    }
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find(d => Number(d.deviceId || d.id) === Number(deviceId));
    if (!device) return `Device ${deviceId}`;

    const rawName = (device.deviceName || device.name || '').trim();
    if (rawName) {
      const [firstPart] = rawName.split('-');
      return (firstPart || '').trim() || rawName;
    }
    return `Device ${deviceId}`;
  };

  const formatTimeOnly = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    // Format: "2026-04-07 20:59:31.107" -> "20:59:31"
    const parts = dateTimeStr.split(' ');
    if (parts.length < 2) return dateTimeStr;
    return parts[1].split('.')[0];
  };

  const getTimeAgo = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    try {
      // Handle the space between date and time
      const isoStr = dateTimeStr.replace(' ', 'T');
      const eventTime = new Date(isoStr).getTime();
      if (isNaN(eventTime)) return '';

      const seconds = Math.floor((now - eventTime) / 1000);
      if (seconds < 60) return `${seconds}s ago`;

      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) return `${minutes}m ${remainingSeconds}s ago`;

      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m ago`;
    } catch (e) {
      return '';
    }
  };

  return (
    <>
      {alerts.map((alert) => {
        const icon = alert.event_code === 'HAZARD_NO' ? hazardOpenIcon : hazardBlockIcon;
        return (
          <Marker
            key={alert.id}
            position={[alert.latitude, alert.longitude]}
            icon={icon}
          >
            <Popup>
              <div style={{ fontSize: '14px', minWidth: '220px', padding: '5px' }}>
                <div style={{
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eee',
                  marginBottom: '8px',
                  paddingBottom: '4px',
                  color: '#d32f2f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>⚠️ HAZARD ALERT</span>
                  <span style={{ fontSize: '11px', color: '#999' }}>#{alert.id}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '85px 1fr', gap: '4px', marginBottom: '12px', lineHeight: '1.4' }}>
                  <span style={{ color: '#666' }}>Device:</span>
                  <span style={{ fontWeight: 'bold' }}>{getDeviceName(alert.device_id)}</span>

                  <span style={{ color: '#666' }}>Time:</span>
                  <span style={{ fontWeight: '500' }}>
                    {formatTimeOnly(alert.event_time)}
                    <div style={{ fontSize: '11px', color: '#cb2431', fontWeight: '400' }}>
                      {getTimeAgo(alert.event_time)}
                    </div>
                  </span>

                  <span style={{ color: '#666' }}>Position:</span>
                  <span style={{ fontSize: '12px', color: '#444' }}>
                    {alert.latitude.toFixed(5)}, {alert.longitude.toFixed(5)}
                  </span>
                </div>

                <button
                  onClick={() => handleDeactivate(alert.id)}
                  disabled={deactivatingId === alert.id}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: deactivatingId === alert.id ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deactivatingId === alert.id ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }}
                >
                  {deactivatingId === alert.id ? 'Removing...' : 'Remove Alert'}
                </button>

                <div style={{
                  fontSize: '10px',
                  textAlign: 'center',
                  color: connected ? '#4caf50' : '#ffa000'
                }}>
                  {connected ? '● Connection active' : '○ Reconnecting...'}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default HazardMarkers;
